import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Sidebar from '../landingPage/Sidebar';
import { FilterSelect } from '../../components/ui/select';
import { PrimaryButton } from '../../components/ui/button';
import { PrimaryBreadcrumb } from '../../components/ui/breadcrumb';
import { fetchTypeBasedConnection } from '../../apis/Connections';
import { fetchModulesBySource } from '../../apis/EntitiesSelection';
import { saveWorkflow } from '../../config/workflowApi';
import { useDispatch } from 'react-redux';
import { addWorkflow } from '../../state/slices/workflowSlice';
import Cookies from 'js-cookie';
import { getConnectorTypeForAPI, getDisplayNameFromConnector } from '../../utils/connectorUtils';

const steps = ['Project', 'Connections', 'Mapping', 'Canvas'];

const TemplateConnections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get data from location state
  const templateData = location.state?.template || null;
  const templateId = location.state?.templateId || null;
  const project = location.state?.project || null;
  const pipelineName = location.state?.pipelineName || '';

  const [sourceConnections, setSourceConnections] = useState([]);
  const [destinationConnections, setDestinationConnections] = useState([]);
  const [selectedSourceConnection, setSelectedSourceConnection] = useState(null);
  const [selectedDestinationConnection, setSelectedDestinationConnection] = useState(null);
  const [sourceConnectionName, setSourceConnectionName] = useState('');
  const [destinationConnectionName, setDestinationConnectionName] = useState('');
  const [sourceModules, setSourceModules] = useState([]);
  const [destinationModules, setDestinationModules] = useState([]);
  const [selectedSourceModule, setSelectedSourceModule] = useState('');
  const [selectedDestinationModule, setSelectedDestinationModule] = useState('');
  const [sourceError, setSourceError] = useState('');
  const [destinationError, setDestinationError] = useState('');
  const [sourceModuleError, setSourceModuleError] = useState('');
  const [destinationModuleError, setDestinationModuleError] = useState('');
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [loadingModules, setLoadingModules] = useState({ source: false, destination: false });
  const [loading, setLoading] = useState(false);
  const [activeStep] = useState(1);

  // Get connector types from template
  // For API calls, SQL should use 'sql' directly (not 'mssql')
  // This matches NodeConfigurationModal.jsx behavior
  const getConnectorTypeForAPICall = (connectorName) => {
    if (!connectorName) return '';
    const normalized = connectorName.toLowerCase().trim();
    
    // Check if it's SQL - use 'sql' directly for API calls
    if (normalized.includes('sql') || normalized === 'sql' || normalized === 'mssql' || normalized === 'sql server' || normalized === 'sqlserver') {
      return 'sql';
    }
    
    // For other connectors, use getConnectorTypeForAPI
    return getConnectorTypeForAPI(connectorName);
  };

  const sourceConnectorType = getConnectorTypeForAPICall(
    templateData?.apiData?.source_connector || templateData?.sourceConnector?.name || ''
  );
  const destinationConnectorType = getConnectorTypeForAPICall(
    templateData?.apiData?.destination_connector || templateData?.destinationConnector?.name || ''
  );

  useEffect(() => {
    if (!templateData && !templateId) {
      toast.error('Template information is missing. Please select a template again.');
      navigate('/pre-made-templates');
      return;
    }

    if (!project || !pipelineName) {
      toast.error('Project and pipeline information is missing.');
      navigate('/pre-made-templates');
      return;
    }

    loadConnections();
  }, [sourceConnectorType, destinationConnectorType]);

  const loadConnections = async () => {
    setLoadingConnections(true);
    try {
      // Fetch source connections
      if (sourceConnectorType) {
        const sourceConn = await fetchTypeBasedConnection(sourceConnectorType);
        setSourceConnections(sourceConn || []);
      }

      // Fetch destination connections
      if (destinationConnectorType) {
        const destConn = await fetchTypeBasedConnection(destinationConnectorType);
        setDestinationConnections(destConn || []);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleSourceConnectionChange = async (_, newValue) => {
    if (newValue) {
      const connection = sourceConnections.find((c) => c.conn_name === newValue);
      if (connection) {
        setSelectedSourceConnection(connection);
        setSourceConnectionName(newValue);
        setSourceError('');
        setSelectedSourceModule('');
        setSourceModuleError('');
        
        // Fetch modules for selected connection
        // Check if SQL node using same pattern as NodeConfigurationModal
        const connectorName = connection.connector_name || connection.connector || '';
        const connectorNameLower = connectorName.toLowerCase();
        const isSqlNode = connectorNameLower.includes('sql');
        const connectorType = getConnectorTypeForAPI(connectorName);
        
        setLoadingModules(prev => ({ ...prev, source: true }));
        try {
          if (isSqlNode) {
            setSourceModules(['SQL']);
          } else {
            const modules = await fetchModulesBySource(connectorType);
            setSourceModules(modules || []);
          }
        } catch (error) {
          console.error('Error loading source modules:', error);
          setSourceModules([]);
        } finally {
          setLoadingModules(prev => ({ ...prev, source: false }));
        }
      }
    } else {
      setSelectedSourceConnection(null);
      setSourceConnectionName('');
      setSelectedSourceModule('');
      setSourceModules([]);
    }
  };

  const handleDestinationConnectionChange = async (_, newValue) => {
    if (newValue) {
      const connection = destinationConnections.find((c) => c.conn_name === newValue);
      if (connection) {
        setSelectedDestinationConnection(connection);
        setDestinationConnectionName(newValue);
        setDestinationError('');
        setSelectedDestinationModule('');
        setDestinationModuleError('');
        
        // Fetch modules for selected connection
        // Check if SQL node using same pattern as NodeConfigurationModal
        const connectorName = connection.connector_name || connection.connector || '';
        const connectorNameLower = connectorName.toLowerCase();
        const isSqlNode = connectorNameLower.includes('sql');
        const connectorType = getConnectorTypeForAPI(connectorName);
        
        setLoadingModules(prev => ({ ...prev, destination: true }));
        try {
          if (isSqlNode) {
            setDestinationModules(['SQL']);
          } else {
            const modules = await fetchModulesBySource(connectorType);
            setDestinationModules(modules || []);
          }
        } catch (error) {
          console.error('Error loading destination modules:', error);
          setDestinationModules([]);
        } finally {
          setLoadingModules(prev => ({ ...prev, destination: false }));
        }
      }
    } else {
      setSelectedDestinationConnection(null);
      setDestinationConnectionName('');
      setSelectedDestinationModule('');
      setDestinationModules([]);
    }
  };

  const validateConnections = () => {
    let hasError = false;

    if (!selectedSourceConnection) {
      setSourceError('Please select a source connection');
      hasError = true;
    } else {
      setSourceError('');
    }

    if (!selectedSourceModule) {
      setSourceModuleError('Please select a source module');
      hasError = true;
    } else {
      setSourceModuleError('');
    }

    if (!selectedDestinationConnection) {
      setDestinationError('Please select a destination connection');
      hasError = true;
    } else {
      setDestinationError('');
    }

    if (!selectedDestinationModule) {
      setDestinationModuleError('Please select a destination module');
      hasError = true;
    } else {
      setDestinationModuleError('');
    }

    // Validate connector types match template
    // Normalize connector names for comparison (remove spaces, hyphens, case-insensitive)
    const normalizeConnectorName = (name) => {
      return (name || '').toLowerCase().replace(/[\s-_]/g, '');
    };

    if (selectedSourceConnection) {
      const connType = normalizeConnectorName(
        selectedSourceConnection.connector_name || 
        selectedSourceConnection.connector || 
        selectedSourceConnection.connector_type ||
        ''
      );
      const templateType = normalizeConnectorName(sourceConnectorType);
      
      // Check if connector type matches (allowing partial matches for flexibility)
      if (templateType && connType && !connType.includes(templateType) && !templateType.includes(connType)) {
        // Try to match common connector name variations
        const commonMatches = {
          'salesforce': ['sf', 'salesforce'],
          'mssql': ['sqlserver', 'mssql', 'sql'],
          'postgresql': ['postgres', 'postgresql', 'pg'],
          'mysql': ['mysql', 'maria'],
          'bigquery': ['bigquery', 'bq', 'gcp'],
          'snowflake': ['snowflake', 'sf'],
          'redshift': ['redshift', 'aws'],
        };
        
        let matched = false;
        for (const [key, variations] of Object.entries(commonMatches)) {
          if (templateType.includes(key) || variations.some(v => templateType.includes(v))) {
            if (variations.some(v => connType.includes(v)) || connType.includes(key)) {
              matched = true;
              break;
            }
          }
        }
        
        if (!matched) {
          setSourceError(`Connection type must match template source connector: ${sourceConnectorType}`);
          hasError = true;
        }
      }
    }

    if (selectedDestinationConnection) {
      const connType = normalizeConnectorName(
        selectedDestinationConnection.connector_name || 
        selectedDestinationConnection.connector || 
        selectedDestinationConnection.connector_type ||
        ''
      );
      const templateType = normalizeConnectorName(destinationConnectorType);
      
      // Check if connector type matches (allowing partial matches for flexibility)
      if (templateType && connType && !connType.includes(templateType) && !templateType.includes(connType)) {
        // Try to match common connector name variations
        const commonMatches = {
          'salesforce': ['sf', 'salesforce'],
          'mssql': ['sqlserver', 'mssql', 'sql'],
          'postgresql': ['postgres', 'postgresql', 'pg'],
          'mysql': ['mysql', 'maria'],
          'bigquery': ['bigquery', 'bq', 'gcp'],
          'snowflake': ['snowflake', 'sf'],
          'redshift': ['redshift', 'aws'],
        };
        
        let matched = false;
        for (const [key, variations] of Object.entries(commonMatches)) {
          if (templateType.includes(key) || variations.some(v => templateType.includes(v))) {
            if (variations.some(v => connType.includes(v)) || connType.includes(key)) {
              matched = true;
              break;
            }
          }
        }
        
        if (!matched) {
          setDestinationError(`Connection type must match template destination connector: ${destinationConnectorType}`);
          hasError = true;
        }
      }
    }

    return !hasError;
  };

  const handleContinue = async () => {
    if (!validateConnections()) {
      return;
    }

    const userId = Cookies.get('userId');
    if (!userId) {
      toast.error('User session expired. Please login again.');
      return;
    }

    try {
      setLoading(true);

      // Create workflow with template data
      const workflowData = {
        project_id: project.project_id,
        user_id: userId,
        name: pipelineName,
        environment: 'test',
        is_active: true,
        description: templateData?.description || 'Created from template',
        template_id: templateId || templateData?.id,
        source_connection: {
          conn_id: selectedSourceConnection.conn_id,
          conn_name: selectedSourceConnection.conn_name,
          connector_name: selectedSourceConnection.connector_name || selectedSourceConnection.connector,
        },
        destination_connection: {
          conn_id: selectedDestinationConnection.conn_id,
          conn_name: selectedDestinationConnection.conn_name,
          connector_name: selectedDestinationConnection.connector_name || selectedDestinationConnection.connector,
        },
      };

      const savedWorkflow = await saveWorkflow(workflowData);
      dispatch(addWorkflow(savedWorkflow));

      toast.success('Pipeline created successfully!', {
        position: 'top-right',
        autoClose: 2000,
      });

      // Normalize modules: only SQL modules should be lowercase, others preserve original case
      // This matches the JSON structure where modules like "Salesforce Financial Services Cloud" 
      // should be stored with their original capitalization
      const sourceConnectorName = selectedSourceConnection?.connector_name || selectedSourceConnection?.connector || '';
      const isSourceSql = sourceConnectorName.toLowerCase().includes('sql');
      const normalizedSourceModule = selectedSourceModule 
        ? (isSourceSql ? selectedSourceModule.toLowerCase().trim() : selectedSourceModule.trim())
        : '';
      
      const destConnectorName = selectedDestinationConnection?.connector_name || selectedDestinationConnection?.connector || '';
      const isDestSql = destConnectorName.toLowerCase().includes('sql');
      const normalizedDestinationModule = selectedDestinationModule 
        ? (isDestSql ? selectedDestinationModule.toLowerCase().trim() : selectedDestinationModule.trim())
        : '';

      // Navigate to workflow canvas with template data
      setTimeout(() => {
        navigate(`/parentworkflow/${savedWorkflow?.workflow_id}`, {
          state: {
            fromTemplate: true,
            templateId: templateId || templateData?.id,
            templateData: templateData,
            sourceConnection: selectedSourceConnection,
            destinationConnection: selectedDestinationConnection,
            sourceModule: normalizedSourceModule,
            destinationModule: normalizedDestinationModule,
          },
        });
      }, 2000);
    } catch (error) {
      console.error('Error creating workflow from template:', error);
      toast.error('Error creating pipeline. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/pre-made-templates/setup', {
      state: {
        template: templateData,
        templateId: templateId,
      },
    });
  };

  const handleCancel = () => {
    if (templateId) {
      navigate(`/pre-made-templates/${templateId}`);
    } else {
      navigate('/pre-made-templates');
    }
  };

  const sourceOptions = sourceConnections.map((conn) => conn.conn_name);
  const destinationOptions = destinationConnections.map((conn) => conn.conn_name);

  const templateTitle = templateData?.title || templateData?.name || 'Template';

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        background: '#FAFBFD',
        minHeight: '100vh',
      }}
    >
      <Sidebar />

      <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <PrimaryBreadcrumb
          items={[
            {
              label: 'Pre-Made Templates',
              path: '/pre-made-templates',
            },
            {
              label: templateTitle,
              path: templateId ? `/pre-made-templates/${templateId}` : '/pre-made-templates',
            },
            {
              label: 'Configure Connections',
            },
          ]}
        />

        {/* Step Indicator */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: activeStep === steps.indexOf(label) ? 600 : 400,
                      fontSize: { xs: '0.9375rem', sm: '0.875rem', md: '1rem' },
                      color: activeStep === steps.indexOf(label) ? '#3B9562' : '#9CA3AF',
                    },
                    '& .MuiStepIcon-root': {
                      width: { xs: 25, sm: 26, md: 30 },
                      height: { xs: 25, sm: 26, md: 30 },
                      color: activeStep === steps.indexOf(label) ? '#10B981' : '#E5E7EB',
                      '&.Mui-active': {
                        color: '#3B9562',
                      },
                      '&.Mui-completed': {
                        color: '#3B9562',
                      },
                      '& .MuiStepIcon-text': {
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', sm: '65%', md: '60%' },
            maxWidth: '800px',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: '8px',
              border: '1px solid #E6E6E6',
              background: '#FFFFFF',
              boxShadow: '0px 0px 6px 0px #7878781A',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '1.25rem',
                lineHeight: '1.875rem',
                letterSpacing: '-0.45px',
                color: '#0A0A0A',
                mb: 1,
              }}
            >
              Configure Connections
            </Typography>

            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '0.875rem',
                lineHeight: '100%',
                letterSpacing: '-0.15px',
                color: '#4A5565',
                mb: 3,
              }}
            >
              Select source and destination connections for your pipeline.
            </Typography>

            {/* Source Connection */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#383838',
                  mb: 1,
                }}
              >
                Source Connection
                {sourceConnectorType && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      color: '#6B7280',
                      ml: 1,
                    }}
                  >
                    ({getDisplayNameFromConnector(templateData?.apiData?.source_connector || templateData?.sourceConnector?.name || sourceConnectorType)})
                  </Typography>
                )}
              </Typography>
              {loadingConnections ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <CircularProgress size={20} />
                  <Typography
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontSize: '0.875rem',
                      color: '#6B7280',
                    }}
                  >
                    Loading connections...
                  </Typography>
                </Box>
              ) : (
                <>
                  <FilterSelect
                    type="autocomplete"
                    options={sourceOptions}
                    value={sourceConnectionName}
                    onChange={handleSourceConnectionChange}
                    placeholder="Select Source Connection"
                    sx={{
                      width: '100%',
                      background: '#FFFFFF',
                      '& .MuiAutocomplete-root': {
                        width: '100%',
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                      },
                    }}
                  />
                  {sourceError && (
                    <Typography
                      sx={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        mt: 0.5,
                        ml: 1.25,
                      }}
                    >
                      {sourceError}
                    </Typography>
                  )}
                  {!sourceError && sourceConnections.length === 0 && (
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '0.8125rem',
                        lineHeight: '100%',
                        letterSpacing: '-0.15px',
                        color: '#EF4444',
                        mt: 0.5,
                      }}
                    >
                      No {sourceConnectorType} connections found. Please create a connection first.
                    </Typography>
                  )}
                </>
              )}
              
              {/* Source Module Selection */}
              {selectedSourceConnection && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#383838',
                      mb: 1,
                    }}
                  >
                    Select Module
                  </Typography>
                  {loadingModules.source ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                      <CircularProgress size={20} />
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontSize: '0.875rem',
                          color: '#6B7280',
                        }}
                      >
                        Loading modules...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <FilterSelect
                        type="autocomplete"
                        options={sourceModules}
                        value={selectedSourceModule}
                        onChange={(_, newValue) => {
                          setSelectedSourceModule(newValue || '');
                          setSourceModuleError('');
                        }}
                        placeholder="Select Source Module"
                        sx={{
                          width: '100%',
                          background: '#FFFFFF',
                          '& .MuiAutocomplete-root': {
                            width: '100%',
                          },
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      />
                      {sourceModuleError && (
                        <Typography
                          sx={{
                            color: '#ef4444',
                            fontSize: '0.75rem',
                            mt: 0.5,
                            ml: 1.25,
                          }}
                        >
                          {sourceModuleError}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* Destination Connection */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#383838',
                  mb: 1,
                }}
              >
                Destination Connection
                {destinationConnectorType && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      color: '#6B7280',
                      ml: 1,
                    }}
                  >
                    ({getDisplayNameFromConnector(templateData?.apiData?.destination_connector || templateData?.destinationConnector?.name || destinationConnectorType)})
                  </Typography>
                )}
              </Typography>
              {loadingConnections ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <CircularProgress size={20} />
                  <Typography
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontSize: '0.875rem',
                      color: '#6B7280',
                    }}
                  >
                    Loading connections...
                  </Typography>
                </Box>
              ) : (
                <>
                  <FilterSelect
                    type="autocomplete"
                    options={destinationOptions}
                    value={destinationConnectionName}
                    onChange={handleDestinationConnectionChange}
                    placeholder="Select Destination Connection"
                    sx={{
                      width: '100%',
                      background: '#FFFFFF',
                      '& .MuiAutocomplete-root': {
                        width: '100%',
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                      },
                    }}
                  />
                  {destinationError && (
                    <Typography
                      sx={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        mt: 0.5,
                        ml: 1.25,
                      }}
                    >
                      {destinationError}
                    </Typography>
                  )}
                  {!destinationError && destinationConnections.length === 0 && (
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '0.8125rem',
                        lineHeight: '100%',
                        letterSpacing: '-0.15px',
                        color: '#EF4444',
                        mt: 0.5,
                      }}
                    >
                      No {destinationConnectorType} connections found. Please create a connection first.
                    </Typography>
                  )}
                </>
              )}
              
              {/* Destination Module Selection */}
              {selectedDestinationConnection && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#383838',
                      mb: 1,
                    }}
                  >
                    Select Module
                  </Typography>
                  {loadingModules.destination ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                      <CircularProgress size={20} />
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontSize: '0.875rem',
                          color: '#6B7280',
                        }}
                      >
                        Loading modules...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <FilterSelect
                        type="autocomplete"
                        options={destinationModules}
                        value={selectedDestinationModule}
                        onChange={(_, newValue) => {
                          setSelectedDestinationModule(newValue || '');
                          setDestinationModuleError('');
                        }}
                        placeholder="Select Destination Module"
                        sx={{
                          width: '100%',
                          background: '#FFFFFF',
                          '& .MuiAutocomplete-root': {
                            width: '100%',
                          },
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      />
                      {destinationModuleError && (
                        <Typography
                          sx={{
                            color: '#ef4444',
                            fontSize: '0.75rem',
                            mt: 0.5,
                            ml: 1.25,
                          }}
                        >
                          {destinationModuleError}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                pt: 2,
                borderTop: '1px solid #E6E6E6',
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <PrimaryButton
                  variant="outlined"
                  onClick={handleCancel}
                >
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  variant="outlined"
                  onClick={handleBack}
                >
                  Back
                </PrimaryButton>
              </Box>
              <PrimaryButton
                onClick={handleContinue}
                disabled={!selectedSourceConnection || !selectedSourceModule || !selectedDestinationConnection || !selectedDestinationModule || loading}
              >
                {loading ? 'Creating...' : 'Continue'}
              </PrimaryButton>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateConnections;
